// src/components/reports/EmergencyCards.tsx
import { useEffect, useState } from "react";
import { useReportsStore } from "@/store/reportsStore";
import { useAuthStore } from "@/store/authStore";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Trash2, Edit, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import EmergencyForm from "./EmergencyForm";
import type { Emergency } from "@/types/report";

export default function EmergencyCards() {
  const { emergencies, fetchData, deleteEmergency, deletePhotoFromEmergency } = useReportsStore();
  const { user } = useAuthStore();

  // Самая надёжная проверка — работает с "4" и 4
  const isPavlik = user?.id != null && String(user.id) === "4";

  const [selectedEmergency, setSelectedEmergency] = useState<Emergency | null>(null);
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDateRange = (start?: string, end?: string) => {
    if (!start && !end) return "—";
    const f = (d?: string) => d ? d.split("-").reverse().join(".") : "?";
    return `${f(start)}–${f(end)}`;
  };

  const handleDeletePhoto = async (photoUrl: string) => {
    if (!selectedEmergency?.id) return;
    if (!window.confirm("Удалить это фото?")) return;

    try {
      await deletePhotoFromEmergency(selectedEmergency.id, photoUrl);
      setSelectedEmergency(prev =>
        prev ? { ...prev, photoUrls: prev.photoUrls?.filter(u => u !== photoUrl) } : null
      );
    } catch {}
  };

  return (
    <>
      <div className="mt-12 md:mt-16 px-4 md:px-0">
        <h2 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-300 via-pink-300 to-cyan-300 mb-12 text-center md:text-left tracking-tight">
          Чрезвычайные ситуации
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
          {emergencies.map((emergency) => (
            <div
              key={emergency.id}
              onClick={() => setSelectedEmergency(emergency)}
              className={`
                group relative overflow-hidden
                bg-gradient-to-br from-purple-950 to-indigo-950
                rounded-3xl border border-purple-700/20
                shadow-xl shadow-purple-950/30
                transition-all duration-300 hover:shadow-purple-700/50 hover:scale-[1.03]
                cursor-pointer
                min-h-[180px]          // ← НОВАЯ МИНИМАЛЬНАЯ ВЫСОТА
                max-h-[260px]          // ← НОВЫЙ МАКСИМУМ ВЫСОТЫ
                flex flex-col justify-center
              `}
            >
              {/* Бейдж "ЧС" */}
              <Badge className="absolute top-5 right-5 bg-purple-800/90 text-white text-sm px-3 py-1 rounded-md backdrop-blur-sm border-none shadow-md z-10">
                ЧС
              </Badge>

              {/* Основной контент */}
              <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-6 line-clamp-2">
                  {emergency.name}
                </h3>

                <div className="text-8xl md:text-9xl font-black text-cyan-400 tracking-tighter drop-shadow-2xl leading-none">
                  {emergency.volunteers}
                </div>

                <p className="text-xl md:text-2xl text-cyan-300/90 mt-4 font-medium">
                  добровольцев
                </p>
              </div>

              {/* Дата внизу */}
              <div className="absolute bottom-4 left-0 right-0 text-center text-purple-300/80 text-sm font-medium">
                {formatDateRange(emergency.startDate, emergency.endDate)}
              </div>

              {/* Корзина ТОЛЬКО у Павлик */}
              {isPavlik && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute bottom-4 left-4 text-red-400 hover:text-red-300 hover:bg-red-950/40 rounded-full p-2 transition-colors z-10"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (window.confirm(`Удалить "${emergency.name}"?`)) {
                      deleteEmergency(emergency.id);
                    }
                  }}
                >
                  <Trash2 className="h-5 w-5" />
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Модальное окно */}
      <Dialog open={!!selectedEmergency} onOpenChange={() => { setSelectedEmergency(null); setEditMode(false); }}>
        <DialogContent className="sm:max-w-[1100px] lg:max-w-[1200px] bg-gradient-to-b from-purple-950 to-indigo-950 border-purple-700/50 text-white max-h-[92vh] overflow-hidden p-0 rounded-3xl">
          {selectedEmergency && (
            <>
              <DialogHeader className="p-6 pb-4 border-b border-purple-700/40 flex flex-row justify-between items-center">
                <DialogTitle className="text-3xl font-bold text-purple-100">
                  {editMode ? "Редактирование" : selectedEmergency.name}
                </DialogTitle>

                {isPavlik && !editMode && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditMode(true)}
                    className={`
                      bg-gradient-to-r from-purple-600 to-pink-600
                      hover:from-purple-700 hover:to-pink-700
                      text-white shadow-lg shadow-purple-600/30
                      border border-purple-500/30
                      px-6 py-2 text-base font-medium rounded-xl
                      flex items-center gap-2 transition-all duration-300 hover:scale-105 hover:shadow-purple-700/50
                    `}
                  >
                    <Edit className="h-5 w-5" />
                    Редактировать
                  </Button>
                )}
              </DialogHeader>

              {editMode ? (
                <div className="p-6 md:p-8 overflow-y-auto max-h-[calc(92vh-140px)] custom-scrollbar">
                  <EmergencyForm
                    onClose={() => {
                      setEditMode(false);
                      // можно обновить selectedEmergency, если нужно
                    }}
                    initialData={selectedEmergency}
                  />
                </div>
              ) : (
                <div className="overflow-y-auto max-h-[calc(92vh-140px)] custom-scrollbar">
                  <div className="flex flex-col md:flex-row min-h-full">
                    {/* Левая колонка */}
                    <div className="md:w-3/5 p-8 space-y-10">
                      <div>
                        <h4 className="text-2xl font-semibold text-purple-200 mb-4">
                          Количество добровольцев ВСКС
                        </h4>
                        <p className="text-7xl font-black text-cyan-300 drop-shadow-2xl">
                          {selectedEmergency.volunteers}
                        </p>
                        <p className="text-2xl text-cyan-200/90 mt-2">принявших участие</p>
                      </div>

                      <div>
                        <h4 className="text-2xl font-semibold text-purple-200 mb-4">
                          Дата проведения ЧС
                        </h4>
                        <p className="text-3xl font-bold text-purple-100">
                          {formatDateRange(selectedEmergency.startDate, selectedEmergency.endDate)}
                        </p>
                      </div>

                      {selectedEmergency.region && (
                        <div>
                          <h4 className="text-2xl font-semibold text-purple-200 mb-4">Регион</h4>
                          <p className="text-2xl text-purple-100">{selectedEmergency.region}</p>
                        </div>
                      )}

                      <div>
                        <h4 className="text-2xl font-semibold text-purple-200 mb-4">Статус</h4>
                        <Badge className="text-xl px-6 py-3 bg-purple-900/60 border-purple-500/50">
                          {selectedEmergency.status || "Не указан"}
                        </Badge>
                      </div>

                      <div>
                        <h4 className="text-3xl font-bold text-purple-100 mb-5">История ЧС</h4>
                        {selectedEmergency.history ? (
                          <div className="bg-purple-950/70 border border-purple-600/40 rounded-2xl p-8">
                            <p className="text-xl leading-relaxed text-purple-100 whitespace-pre-wrap">
                              {selectedEmergency.history}
                            </p>
                          </div>
                        ) : (
                          <div className="text-purple-400 italic text-xl bg-purple-950/50 p-8 rounded-2xl border border-purple-700/30">
                            Описание отсутствует
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Правая колонка — фото */}
                    <div className="md:w-2/5 bg-purple-950/80 border-l border-purple-700/40 p-8">
                      <h4 className="text-3xl font-bold text-purple-100 mb-6">Фото</h4>

                      {selectedEmergency.photoUrls?.length ? (
                        <div className="grid grid-cols-2 gap-5">
                          {selectedEmergency.photoUrls.map((url, idx) => (
                            <div key={idx} className="relative aspect-square rounded-2xl overflow-hidden border-2 border-purple-600/50 shadow-2xl shadow-purple-900/30 group/photo">
                              <img src={url} alt={`Фото ${idx + 1}`} className="w-full h-full object-cover transition-transform duration-500 group-hover/photo:scale-110" />
                              {isPavlik && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="absolute top-3 right-3 bg-red-900/80 backdrop-blur-sm hover:bg-red-800 text-white rounded-full opacity-0 group-hover/photo:opacity-100 transition-opacity duration-300"
                                  onClick={() => handleDeletePhoto(url)}
                                >
                                  <X className="h-6 w-6" />
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="h-80 bg-purple-950/50 rounded-2xl flex items-center justify-center text-purple-400 border border-purple-700/40">
                          <div className="text-center p-10">
                            <p className="text-2xl font-medium">Фото отсутствуют</p>
                            <p className="text-lg mt-4 opacity-80">Добавьте при создании или редактировании</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}